// /src/pages/Consents/ImportConsentModal.js
import React, { useState } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  LinearProgress,
  Divider
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";

export default function ImportConsentModal({ onSuccess, onCancel }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // 1. Generate and Download Sample Excel (CSV)
  const downloadSample = () => {
    // CSV Header
    const headers = ["email", "phone", "promotional_consent", "exclusive_deals_consent", "terms_consent"];
    // Sample Data Rows
    const rows = [
      ["user1@example.com", "", "1", "0", "1"],
      ["", "+1234567890", "0", "1", "1"],
      ["user2@example.com", "+1987654321", "1", "1", "1"]
    ];

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "sample_consent_import.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError("");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file first.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setLoading(true);
    try {
      // NOTE: You need to create this endpoint in your backend or use an existing bulk endpoint
      // If no bulk endpoint exists yet, this assumes /user/subscribe/bulk or similar
      await axiosInstance.post("/api/user/subscriptions/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSuccess("Contacts imported successfully!");
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Failed to import file. Please check format.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Upload a CSV or Excel file to bulk import consents. Ensure explicit consent has been granted.
      </Alert>

      {/* Guide Section */}
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        File Structure Guide
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "action.hover" }}>
            <TableRow>
              <TableCell><strong>Column Name</strong></TableCell>
              <TableCell><strong>Required?</strong></TableCell>
              <TableCell><strong>Format/Value</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>email</TableCell>
              <TableCell>Yes (or Phone)</TableCell>
              <TableCell>valid@email.com</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>phone</TableCell>
              <TableCell>Yes (or Email)</TableCell>
              <TableCell>E.164 (e.g., +15551234567)</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>promotional_consent</TableCell>
              <TableCell>No</TableCell>
              <TableCell>1 (Yes) or 0 (No)</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>exclusive_deals_consent</TableCell>
              <TableCell>No</TableCell>
              <TableCell>1 (Yes) or 0 (No)</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>terms_consent</TableCell>
              <TableCell><strong>Yes</strong></TableCell>
              <TableCell>1 (Must be 1)</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />} 
            onClick={downloadSample}
        >
          Download Sample CSV
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }}>Upload File</Divider>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box 
        sx={{ 
            border: '2px dashed #ccc', 
            borderRadius: 2, 
            p: 4, 
            textAlign: 'center',
            bgcolor: 'background.default',
            cursor: 'pointer'
        }}
        onClick={() => document.getElementById('fileInput').click()}
      >
        <input
            id="fileInput"
            type="file"
            accept=".csv, .xlsx, .xls"
            style={{ display: 'none' }}
            onChange={handleFileChange}
        />
        <UploadFileIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
        <Typography variant="body1">
            {file ? file.name : "Click to select file or drag here"}
        </Typography>
        <Typography variant="caption" color="textSecondary">
            Supports .csv, .xlsx
        </Typography>
      </Box>

      {loading && <LinearProgress sx={{ mt: 2 }} />}

      <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button 
            variant="contained" 
            onClick={handleUpload} 
            disabled={!file || loading}
        >
          {loading ? "Importing..." : "Upload & Import"}
        </Button>
      </Box>
    </Box>
  );
}